"use client";

import { useState } from "react";
import { Modal } from "@/components/modal";
import CustomSelect from "@/components/custom-select";
import { XCircle } from "@/lib/icons";
import { $n, $k } from "@/lib/format";
import { OPTION_MULTIPLIER } from "@/lib/option-multiplier";

/** The spread fields the composite payoff is drawn from. */
export interface CompositeTrade {
  symbol: string;
  spreadType: string;
  shortStrikePrice: number;
  longStrikePrice: number;
  netPremium: number;
  contracts: number;
  shortOptionSymbol: string;
}

/**
 * Composite P&L — sum of every open spread's payoff per underlying. Plots a
 * curve at terminal-payoff prices (each spread held to its own expiration);
 * the visualisation is most useful when multiple spreads share an underlying
 * (laddered or iron-condor shapes pop out immediately).
 */
export function CompositePnlModal({
  open,
  onClose,
  trades: allTrades,
  spotFor,
}: {
  open: boolean;
  onClose: () => void;
  trades: CompositeTrade[];
  /** Live spot for an underlying (the yellow line and the short-strike % labels). */
  spotFor: (underlying: string, trades: CompositeTrade[]) => number | null;
}) {
  const [compositeUndl, setCompositeUndl] = useState<string | null>(null);
  return (
    <Modal open={open} onClose={onClose} width="800px" maxWidth="95vw">
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-amber text-base">Composite P&L — All Open Positions</span>
        <button onClick={onClose} className="text-foreground/60 hover:text-foreground"><XCircle className="w-[18px] h-[18px]" /></button>
      </div>
      {(() => {
        if (allTrades.length === 0) {
          return (
            <div className="inline-block rounded-lg border border-border bg-surface px-3 py-2 mb-[10px]">
              <p className="text-xs text-muted">No open positions.</p>
            </div>
          );
        }
        // Group spreads by underlying — composite is per-underlying since
        // each chart's x-axis is the underlying's price.
        const groups = new Map<string, CompositeTrade[]>();
        for (const t of allTrades) {
          const arr = groups.get(t.symbol) ?? [];
          arr.push(t);
          groups.set(t.symbol, arr);
        }
        const niceStep = (range: number, target: number) => {
          const raw = range / target;
          if (raw <= 0) return 1;
          const mag = Math.pow(10, Math.floor(Math.log10(raw)));
          const n = raw / mag;
          return (n < 1.5 ? 1 : n < 3 ? 2 : n < 7 ? 5 : 10) * mag;
        };
        // Per-share payoff at terminal underlying price S for one spread.
        const payoffPs = (S: number, t: CompositeTrade) => {
          const isPut = t.spreadType === "bull_put";
          const sK = t.shortStrikePrice, lK = t.longStrikePrice;
          const w = Math.abs(sK - lK), c = t.netPremium;
          if (isPut) {
            if (S >= sK) return c;
            if (S <= lK) return -(w - c);
            return c - (sK - S);
          } else {
            if (S <= sK) return c;
            if (S >= lK) return -(w - c);
            return c - (S - sK);
          }
        };
        const undlList = [...groups.keys()].sort();
        const activeUndl = (compositeUndl && groups.has(compositeUndl)) ? compositeUndl : undlList[0];
        const activeTrades = groups.get(activeUndl) ?? [];
        return (
          <>
            {undlList.length > 1 && (
              <div className="flex items-center gap-3 mb-3 text-xs">
                <span className="text-muted">Underlying</span>
                <CustomSelect
                  value={activeUndl}
                  onChange={(v) => setCompositeUndl(v)}
                  options={undlList.map((u) => ({
                    value: u,
                    label: `${u} (${groups.get(u)!.length} ${groups.get(u)!.length === 1 ? "spread" : "spreads"})`,
                  }))}
                />
              </div>
            )}
            {[[activeUndl, activeTrades] as const].map(([underlying, trades]) => {
              // Live spot — re-read every parent render so the yellow line and
              // top short-strike % labels both track the index/equity last.
              const spot = spotFor(underlying, trades);
              const allK: number[] = [];
              for (const t of trades) allK.push(t.shortStrikePrice, t.longStrikePrice);
              const refLo = Math.min(...allK, spot ?? Infinity);
              const refHi = Math.max(...allK, spot ?? -Infinity);
              const maxWidth = Math.max(...trades.map((t) => Math.abs(t.shortStrikePrice - t.longStrikePrice)));
              const pad = Math.max(maxWidth * 2, (refHi - refLo) * 0.15);
              const lo = refLo - pad, hi = refHi + pad;
              const steps = 200;
              const points: { price: number; pnl: number }[] = [];
              for (let i = 0; i <= steps; i++) {
                const S = lo + (hi - lo) * (i / steps);
                let total = 0;
                for (const t of trades) total += payoffPs(S, t) * OPTION_MULTIPLIER * t.contracts;
                points.push({ price: S, pnl: total });
              }
              const chartW = 720, chartH = 320, padL = 55, padR = 30, padT = 30, padB = 50;
              const drawW = chartW - padL - padR, drawH = chartH - padT - padB;
              const minPnl = Math.min(...points.map((p) => p.pnl));
              const maxPnl = Math.max(...points.map((p) => p.pnl));
              const pnlRange = maxPnl - minPnl || 1;
              const x = (price: number) => padL + ((price - lo) / (hi - lo)) * drawW;
              const y = (pnl: number) => padT + drawH - ((pnl - minPnl) / pnlRange) * drawH;
              const zeroY = y(0);
              const baselineY = chartH - padB;
              const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.price).toFixed(1)},${y(p.pnl).toFixed(1)}`).join(" ");
              const xStep = niceStep(hi - lo, 7);
              const xFirst = Math.ceil(lo / xStep) * xStep;
              const xTicks: number[] = [];
              for (let v = xFirst; v <= hi + 1e-9; v += xStep) xTicks.push(+v.toFixed(6));
              const yStep = niceStep(pnlRange, 5);
              const yFirst = Math.ceil(minPnl / yStep) * yStep;
              const yTicks: number[] = [];
              for (let v = yFirst; v <= maxPnl + 1e-9; v += yStep) yTicks.push(+v.toFixed(2));
              // Split points into contiguous profit/loss runs so the fills
              // come out clean on iron-condor-shaped curves (profit middle,
              // loss outsides).
              const runs = (pred: (p: { pnl: number }) => boolean) => {
                const out: typeof points[] = [];
                let cur: typeof points = [];
                for (const p of points) {
                  if (pred(p)) cur.push(p);
                  else if (cur.length) { out.push(cur); cur = []; }
                }
                if (cur.length) out.push(cur);
                return out;
              };
              const profitRuns = runs((p) => p.pnl > 0);
              const lossRuns = runs((p) => p.pnl < 0);
              const totalAtSpot = spot != null
                ? trades.reduce((s, t) => s + payoffPs(spot, t) * OPTION_MULTIPLIER * t.contracts, 0)
                : null;
              return (
                <div key={underlying} className="mb-5">
                  <div className="flex items-center gap-3 mb-1 text-xs">
                    <span className="font-semibold text-amber text-sm">{underlying}</span>
                    <span className="text-muted">{trades.length} {trades.length === 1 ? "spread" : "spreads"}</span>
                    <span className="ml-auto text-muted">
                      Max profit <span className="text-green">${$n(maxPnl)}</span> · Max loss <span className="text-red">${$n(minPnl)}</span>
                      {totalAtSpot != null && (<> · At spot <span style={{ color: totalAtSpot >= 0 ? "#4ade80" : "#f87171" }}>{totalAtSpot >= 0 ? "+" : ""}${$n(Math.abs(totalAtSpot))}</span></>)}
                    </span>
                  </div>
                  <svg width={chartW} height={chartH}>
                    {/* zero line */}
                    <line x1={padL} x2={chartW - padR} y1={zeroY} y2={zeroY} stroke="var(--border)" strokeDasharray="3,3" />
                    {/* gridlines + bottom ($) axis — dollar prices at sane intervals */}
                    <line x1={padL} x2={chartW - padR} y1={baselineY} y2={baselineY} stroke="#e5e7eb" strokeWidth="1" />
                    {xTicks.map((v) => (
                      <g key={`xt-${v}`}>
                        <line x1={x(v)} x2={x(v)} y1={padT} y2={baselineY} stroke="#3f3f46" strokeDasharray="2,3" />
                        <line x1={x(v)} x2={x(v)} y1={baselineY} y2={baselineY + 6} stroke="#e5e7eb" />
                        <text x={x(v)} y={baselineY + 20} textAnchor="middle" fill="#e5e7eb" fontSize="11" fontWeight="600" fontFamily="monospace">{v.toLocaleString("en-US", { maximumFractionDigits: 2 })}</text>
                      </g>
                    ))}
                    {/* spot price in amber, below the bottom ($) axis at the spot marker */}
                    {spot != null && spot > 0 && (
                      <text x={Math.max(padL + 16, Math.min(chartW - padR - 16, x(spot)))} y={baselineY + 33} textAnchor="middle" fill="var(--amber)" fontSize="10" fontWeight="700" fontFamily="monospace">${$n(spot)}</text>
                    )}
                    {/* y-axis ticks */}
                    {yTicks.map((v) => (
                      <g key={`yt-${v}`}>
                        <line x1={padL - 4} x2={padL} y1={y(v)} y2={y(v)} stroke="#e5e7eb" />
                        <text x={padL - 6} y={y(v) + 4} textAnchor="end" fill="#9ca3af" fontSize="10" fontFamily="monospace">{$k(v)}</text>
                      </g>
                    ))}
                    {/* profit fills */}
                    {profitRuns.map((run, idx) => {
                      if (run.length < 2) return null;
                      const d = run.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.price).toFixed(1)},${y(p.pnl).toFixed(1)}`).join(" ")
                        + ` L${x(run[run.length - 1].price).toFixed(1)},${zeroY.toFixed(1)} L${x(run[0].price).toFixed(1)},${zeroY.toFixed(1)} Z`;
                      return <path key={`pf-${idx}`} d={d} fill="var(--green)" opacity="0.18" />;
                    })}
                    {/* loss fills */}
                    {lossRuns.map((run, idx) => {
                      if (run.length < 2) return null;
                      const d = run.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.price).toFixed(1)},${y(p.pnl).toFixed(1)}`).join(" ")
                        + ` L${x(run[run.length - 1].price).toFixed(1)},${zeroY.toFixed(1)} L${x(run[0].price).toFixed(1)},${zeroY.toFixed(1)} Z`;
                      return <path key={`lf-${idx}`} d={d} fill="var(--red)" opacity="0.18" />;
                    })}
                    {/* strike lines (subtle) */}
                    {trades.flatMap((t, i) => [
                      <line key={`sk-${i}-s`} x1={x(t.shortStrikePrice)} x2={x(t.shortStrikePrice)} y1={padT} y2={baselineY} stroke="#9ca3af" strokeWidth="0.8" opacity="0.45" />,
                      <line key={`sk-${i}-l`} x1={x(t.longStrikePrice)} x2={x(t.longStrikePrice)} y1={padT} y2={baselineY} stroke="#9ca3af" strokeWidth="0.6" opacity="0.3" strokeDasharray="3,2" />,
                    ])}
                    {/* P&L curve */}
                    <path d={pathD} fill="none" stroke="var(--cyan)" strokeWidth="2.5" />
                    {/* spot marker (price is labeled in amber below the bottom axis, not here) */}
                    {spot != null && (
                      <line x1={x(spot)} x2={x(spot)} y1={padT} y2={baselineY} stroke="var(--amber)" strokeWidth="1.5" strokeDasharray="4,2" />
                    )}
                    {/* TOP line: short-strike % from live spot. Recomputed every
                        render as spot ticks; middle-first placement, skip any
                        label whose drawn box would overlap a kept neighbor. */}
                    {spot != null && spot > 0 && (() => {
                      const CHAR_W = 6.6; // ~11px monospace
                      const GAP = 4;
                      const candidates = [...new Set(trades.map((t) => t.shortStrikePrice))]
                        .map((sk) => {
                          const pct = ((sk - spot) / spot) * 100;
                          const label = `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
                          const cx = x(sk);
                          const half = (label.length * CHAR_W) / 2 + GAP / 2;
                          return { sk, label, cx, half, pct };
                        })
                        .sort((a, b) => a.cx - b.cx);
                      if (candidates.length === 0) return null;
                      const mid = Math.floor((candidates.length - 1) / 2);
                      const order = candidates
                        .map((_, i) => i)
                        .sort((a, b) => Math.abs(a - mid) - Math.abs(b - mid) || a - b);
                      const kept: typeof candidates = [];
                      for (const i of order) {
                        const c = candidates[i];
                        if (kept.every((k) => Math.abs(c.cx - k.cx) >= c.half + k.half)) {
                          kept.push(c);
                        }
                      }
                      // key includes label so SVG text remounts when % changes
                      // with spot (some engines are sticky about <text> children).
                      return kept.map((c) => (
                        <text key={`sd-${c.sk}-${c.label}`} x={c.cx} y={padT - 6} textAnchor="middle" fill="#cbd5e1" fontSize="11" fontWeight="600" fontFamily="monospace">{c.label}</text>
                      ));
                    })()}
                  </svg>
                </div>
              );
            })}
          </>
        );
      })()}
    </Modal>
  );
}
